import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Color, RECORD_ROUTES, Size, cents, documentName, isFloorRefusal, t } from '@bms2/shared';
import { Button, Pill, StatusPill, TextInput } from '../../components/controls';
import { useStatusResolver } from '../../shell/StatusResolver';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { PageModule } from '../../shell/PageModule';
import { post, useApi } from '../records/api';
import { line } from '../settings/SettingsShell';
import styles from './PendingApprovalsPage.module.css';
import { Money } from '../../components/Money';
import { useNotice } from '../../components/controls/useNotice';
import { quoteFloorAcknowledgementPath } from '../records/QuoteFloor';
import { FloorOverrideWindow, readQuoteFloors, type OpenFloor } from './FloorOverrideWindow';
import { refreshMenuCounts } from '../../shell/menuCounts';
import { ApprovalDelegationsSection } from './ApprovalDelegations';

/**
 * The Pending Approvals queue at /approvals (piece 8; piece 22; decision
 * 0380), on the To-Do page's pattern: the documents awaiting approval in
 * three sections — the viewer's to decide, everyone else's, and the held ones
 * an administrator resolves — each row carrying the escalation sentence
 * routing wrote. 🔨 Approve and Reject are offered only where canApprove says
 * so, and Reject asks the reason the plan requires before anything is sent.
 */
export const APPROVALS_ROUTE = '/approvals';
export const APPROVALS_PATH = '/approvals/pending';
export const approvalActionPath = (documentType: string, documentId: string, action: 'approve' | 'reject'): string => `/approvals/${documentType}/${documentId}/${action}`;

export interface PendingApprovalRow {
  documentType: string;
  documentId: string;
  number: string | null;
  total: number;
  currencyCode: string;
  submitterName: string;
  escalationReason: string | null;
  awaitingStep: number | null;
  unroutable: boolean;
  canAct: boolean;
  /** False on a request with no money in it, a merge's, whose row shows none (decision 1484); an answer without the key carries an amount. */
  carriesAmount?: boolean;
  /** A merge request's lines: both records side by side, their owners and what will move. */
  detail?: readonly string[];
  /** 🔒 Decision 8960: where the row opens, a File Release's record page; absent or null, the row opens nothing. */
  route?: string | null;
}

export interface PendingApprovalsAnswer {
  count: number;
  rows: readonly PendingApprovalRow[];
}

type Pending = { key: string; reason: string } | null;

const keyOf = (row: PendingApprovalRow) => `${row.documentType}:${row.documentId}`;

/** 🔒 T973: the document's own page, where Approve and Reject are also offered; a type with no page of its own opens nothing. */
function documentPage(row: PendingApprovalRow): string | null {
  const base = (RECORD_ROUTES as Record<string, { record: string } | undefined>)[row.documentType];
  return base ? base.record.replace(':id', row.documentId) : null;
}

/** What stands between a row's number and its amount. */
const NUMBER_AMOUNT_JOIN = ' · ';

/** The approval axis every row in the queue stands at; the queue lists nothing else. */
const APPROVAL_STATUS_WAITING = 'Pending';

export function PendingApprovalsPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const fetched = useApi<PendingApprovalsAnswer>(APPROVALS_PATH, apiBaseUrl);
  const resolveStatus = useStatusResolver();
  const [noticeBanner, setNotice] = useNotice();
  const [rejecting, setRejecting] = useState<Pending>(null);
  const [override, setOverride] = useState<{ row: PendingApprovalRow; floors: OpenFloor[] } | null>(null);
  const rows = useMemo(() => (fetched.state.kind === 'ready' ? fetched.state.data.rows : []), [fetched.state]);
  /**
   * 🔒 An unroutable document stays in the held group whoever is reading
   * (decision 0525), and an administrator sees Approve and Reject on it there.
   * Moving it into the viewer's own group would take the sentence saying why it
   * is held away from the row it is about.
   */
  const mine = rows.filter((row) => row.canAct && !row.unroutable);
  const held = rows.filter((row) => row.unroutable);
  const others = rows.filter((row) => !row.canAct && !row.unroutable);

  const act = async (row: PendingApprovalRow, action: 'approve' | 'reject', body: Record<string, unknown>, done: string) => {
    const answer = await post(approvalActionPath(row.documentType, row.documentId, action), body, apiBaseUrl);
    /*
     * 🔒 An Approve the floor refused asks for the override in the Quote page's own window (T1195; decision 1861).
     * The floors are read with one request, only at this moment, so the queue's own read carries none; a row here
     * is one this viewer may approve, which is who may acknowledge a held Quote's floor.
     */
    if (!answer.ok && action === 'approve' && row.documentType === 'Quote' && isFloorRefusal(answer.body)) {
      const floors = await readQuoteFloors(row.documentId, apiBaseUrl);
      if (floors && floors.length > 0) return setOverride({ row, floors });
    }
    setNotice(answer.ok ? { tone: 'good', text: `${done} · ${documentName(row.documentType)} ${row.number ?? t('approvals.unnumbered')}` } : { tone: 'error', text: answer.message });
    if (answer.ok) {
      setRejecting(null);
      fetched.reload();
      // 🔒 Decision 3072: a decided approval leaves the menu's Approvals balloon at once, not on the next page.
      refreshMenuCounts();
    }
  };

  /** The row's number, a link to the document's own page where it has one. */
  const numberText = (row: PendingApprovalRow) => {
    const text = row.number ?? t('approvals.unnumbered');
    const page = documentPage(row);
    return page ? <Link to={page} className={styles.numberLink} data-approval-number-link={page}>{text}</Link> : <>{text}</>;
  };

  const item = (row: PendingApprovalRow) => {
    const open = rejecting && rejecting.key === keyOf(row) ? rejecting : null;
    return (
      <li key={keyOf(row)} className={styles.item} data-approval={row.documentId} data-approval-type={row.documentType} data-approval-can-act={row.canAct ? 'true' : 'false'}>
        <div className={styles.main}>
          {/*
            🔒 Each row carries its own document-type Pill (piece 12; decision 0599), because the queue
            is one list of six kinds of document and the kind is what a reader sorts by. 🔨 It is the
            tinted non-status chip of decision 0376: a document type is not a state.
          */}
          <span className={styles.title}>
            <span className={styles.typePill} data-approval-type-pill={row.documentType}>
              <Pill text={documentName(row.documentType)} foreground="var(--tag-slate-text)" background="var(--tag-slate-bg)" size={Size.Small} outlined />
            </span>
            {/* 🔒 The row's own state as a pill, read through the one resolver like every list's (decision 0639): every row here is waiting on an approval. */}
            <span data-approval-status-pill={APPROVAL_STATUS_WAITING}>
              <StatusPill entityType="ApprovalStatus" statusValue={APPROVAL_STATUS_WAITING} resolve={resolveStatus} size={Size.Small} />
            </span>
            {row.carriesAmount === false
              ? <>{numberText(row)}</>
              : <>{numberText(row)}{NUMBER_AMOUNT_JOIN}<Money cents={cents(row.total, row.currencyCode).amount} currencyCode={row.currencyCode} /></>}
          </span>
          <span className={styles.meta}>{line(t('approvals.from'), row.submitterName, row.awaitingStep !== null ? `· ${t('approvals.step')} ${row.awaitingStep}` : null)}</span>
          {row.escalationReason ? <span className={styles.meta} data-approval-reason="true">{row.escalationReason}</span> : null}
          {(row.detail ?? []).map((text, index) => <span key={index} className={styles.meta} data-approval-detail="true">{text}</span>)}
          {/* 🔒 Decision 8960: a File Release's row opens the record its file sits on, where the approver reads the file in its place. */}
          {row.route ? <Link to={row.route} className={styles.link} data-approval-open={row.route}>{t('approvals.openRecord')}</Link> : null}
        </div>
        {row.canAct ? (
          <div className={styles.actions}>
            <Button size={Size.Small} color={Color.Navy} text={[t('approvals.approve')]} actions={[() => void act(row, 'approve', {}, t('approvals.approved'))]} />
            <Button size={Size.Small} color={Color.Inactive} text={[t('approvals.reject')]} actions={[() => setRejecting({ key: keyOf(row), reason: '' })]} />
          </div>
        ) : null}
        {open ? (
          <div className={styles.inline} data-approval-reject="true">
            <TextInput size={Size.Large} label={t('approvals.rejectReason')} value={open.reason} onChange={(reason) => setRejecting({ key: keyOf(row), reason })} required />
            <Button size={Size.Small} color={Color.Navy} text={[t('approvals.reject')]} disabled={open.reason.trim() === ''} actions={[() => void act(row, 'reject', { reason: open.reason.trim() }, t('approvals.rejected'))]} />
          </div>
        ) : null}
      </li>
    );
  };

  const section = (name: string, heading: string, list: PendingApprovalRow[]) =>
    list.length > 0 ? (
      <section className={styles.section} data-approvals-section={name}>
        <h2 className={styles.heading}>{heading}</h2>
        <ul className={styles.list}>{list.map(item)}</ul>
      </section>
    ) : null;

  return (
    <PageModule title={t('approvals.title')} subtitle={t('approvals.subtitle')}>
      <div className={styles.page} data-page="approvals">
        {noticeBanner}
        <p className={styles.meta}>{t('approvals.onlyTypes')}</p>
        <AsyncStatePanel state={fetched.state} emptyAction={t('approvals.empty')} onRetry={fetched.reload}>
          {(answer) => (
            <div className={styles.sections} data-approvals-count={answer.count}>
              {answer.rows.length === 0 ? <p className={styles.meta}>{t('approvals.empty')}</p> : null}
              {section('held', t('approvals.unroutable'), held)}
              {section('mine', t('approvals.mine'), mine)}
              {section('others', t('approvals.others'), others)}
            </div>
          )}
        </AsyncStatePanel>
        {/* 🔒 Decisions 10631, 11560 (T3610): an approver's own cover, drawn under the queue on the page every approver opens. */}
        <ApprovalDelegationsSection apiBaseUrl={apiBaseUrl} />
        {override ? (
          <FloorOverrideWindow
            floors={override.floors}
            acknowledgementPath={quoteFloorAcknowledgementPath(override.row.documentId)}
            apiBaseUrl={apiBaseUrl}
            onApprove={async () => {
              const row = override.row;
              setOverride(null);
              await act(row, 'approve', {}, t('approvals.approved'));
            }}
            onCancel={() => setOverride(null)}
            onError={(message) => setNotice({ tone: 'error', text: message })}
          />
        ) : null}
      </div>
    </PageModule>
  );
}
